import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import MomentCard from '../components/MomentCard';
import PostMoment from '../components/PostMoment';
import LoginModal from '../components/LoginModal';
import SkeletonCard from '../components/SkeletonCard';
import ProfileModal from '../components/profilemodal';
import WelcomeModal from '../components/WelcomeModal';

const PAGE_SIZE = 10;

function LazyCard({ children }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ minHeight: visible ? 'auto' : 200 }}>
      {visible ? children : <SkeletonCard />}
    </div>
  );
}

export default function Feed() {
  const navigate = useNavigate();
  const { user, socket, logout } = useApp();
  const [moments, setMoments] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showPost, setShowPost] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [creatingRoom, setCreatingRoom] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [activeTag, setActiveTag] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const loaderRef = useRef(null);

  // ── Show welcome modal once after first signup ──
  useEffect(() => {
    if (!user) return;
    const key = `echomoments_welcomed_${user.id || user.email}`;
    const alreadyWelcomed = localStorage.getItem(key);
    if (!alreadyWelcomed) {
      // Small delay so feed loads first
      const t = setTimeout(() => setShowWelcome(true), 800);
      return () => clearTimeout(t);
    }
  }, [user]);

  const handleWelcomeClose = () => {
    setShowWelcome(false);
    if (user) {
      const key = `echomoments_welcomed_${user.id || user.email}`;
      localStorage.setItem(key, 'true');
    }
  };

  useEffect(() => {
    fetchMoments();
    fetchTags();
  }, []);

  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && visibleCount < moments.length) {
          setLoadingMore(true);
          setTimeout(() => {
            setVisibleCount(prev => Math.min(prev + PAGE_SIZE, moments.length));
            setLoadingMore(false);
          }, 300);
        }
      },
      { rootMargin: '100px' }
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [visibleCount, moments.length]);

  const fetchTags = async () => {
    try {
      const res = await axios.get('https://songmoment.onrender.com/api/moments/tags');
      setAllTags(res.data);
    } catch (err) { console.error(err); }
  };

  const handleTagClick = (tag) => {
    const newTag = activeTag === tag ? null : tag;
    setActiveTag(newTag);
    setVisibleCount(PAGE_SIZE);
    setLoading(true);
    fetchMoments(newTag).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!socket) return;
    socket.on('new_moment', (moment) => setMoments(prev => [moment, ...prev]));
    socket.on('delete_moment', (id) => setMoments(prev => prev.filter(m => m.id !== id)));
    socket.on('moment_reaction_update', ({ moment_id, reactions }) =>
      setMoments(prev => prev.map(m => m.id === moment_id ? { ...m, reactions } : m))
    );
    socket.on('moment_comment_update', ({ moment_id, comment_count_delta }) =>
      setMoments(prev => prev.map(m =>
        m.id === moment_id ? { ...m, comment_count: (m.comment_count || 0) + comment_count_delta } : m
      ))
    );
    return () => {
      socket.off('new_moment');
      socket.off('delete_moment');
      socket.off('moment_reaction_update');
      socket.off('moment_comment_update');
    };
  }, [socket]);

  const fetchMoments = async (tag = null) => {
    try {
      const url = tag
        ? `https://songmoment.onrender.com/api/moments?tag=${encodeURIComponent(tag)}`
        : 'https://songmoment.onrender.com/api/moments';
      const res = await axios.get(url);
      setMoments(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handlePostClick = () => {
    if (!user) setShowLogin(true);
    else setShowPost(true);
  };

  const handleCreateRoom = async () => {
    if (!user) { setShowLogin(true); return; }
    setCreatingRoom(true);
    try {
      const username = user.username
        || user.user_metadata?.username
        || user.email;
      const res = await axios.post('https://songmoment.onrender.com/api/rooms/create', { username });
      navigate(`/room/${res.data.roomId}`);
    } catch (err) {
      console.error('Failed to create room:', err);
    } finally {
      setCreatingRoom(false);
    }
  };

  const visibleMoments = moments.slice(0, visibleCount);

  const navLinkStyle = {
    fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase',
    color: 'rgba(245,230,237,0.45)', cursor: 'pointer',
    background: 'none', border: 'none', padding: 0,
  };

  const navBtnStyle = {
    fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
    color: '#f5e6ed', background: 'transparent',
    border: '0.5px solid rgba(212,83,126,0.6)',
    borderRadius: 20, padding: '6px 16px', cursor: 'pointer',
    whiteSpace: 'nowrap', transition: 'border-color 0.2s, color 0.2s',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#080608' }}>

      <style>{`
        .hide-mobile { display: inline; }
        .show-mobile  { display: none; }
        .moments-grid { display: grid; grid-template-columns: 1fr; gap: 16px; }
        @media (min-width: 900px)  { .moments-grid { grid-template-columns: repeat(2, 1fr); gap: 20px; } }
        @media (min-width: 1300px) { .moments-grid { grid-template-columns: repeat(3, 1fr); gap: 20px; } }
        @media (max-width: 480px) {
          .hide-mobile { display: none; }
          .show-mobile  { display: inline; }
          .logo-text    { font-size: 14px !important; letter-spacing: 0.08em !important; }
          .hero-heading { font-size: 24px !important; margin-bottom: 20px !important; }
          .nav-btn-share { font-size: 10px !important; padding: 5px 10px !important; }
          .nav-link-btn  { font-size: 10px !important; }
          .feed-header   { height: 58px !important; padding: 0 16px !important; }
          .feed-content  { padding: 32px 16px 24px !important; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes glow-pulse {
          0%, 100% { opacity: 1; text-shadow: 0 0 10px #D4537E, 0 0 24px #D4537E; }
          50%       { opacity: 0.6; text-shadow: 0 0 4px #D4537E; }
        }
      `}</style>

      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(8,6,8,0.92)', backdropFilter: 'blur(20px)',
        borderBottom: '0.5px solid rgba(212,83,126,0.2)', padding: '0 24px',
      }}>
        <div className="feed-header" style={{ maxWidth: 1400, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 62, gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg, #D4537E, #993556)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#fff' }}>♪</div>
            <span className="logo-text" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 20, letterSpacing: '0.15em', color: '#f5e6ed', textTransform: 'uppercase' }}>Echo Moments</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
            <button onClick={() => navigate('/about')} className="nav-link-btn" style={navLinkStyle}>
              <span className="hide-mobile">About</span>
            </button>
            <button onClick={() => navigate('/collections')} className="nav-link-btn" style={navLinkStyle}>
              <span className="hide-mobile">Collections</span>
              <span className="show-mobile">🎵</span>
            </button>
            {user ? (
              <>
                <span onClick={() => setShowProfile(true)} className="nav-link-btn" style={{ ...navLinkStyle, color: 'rgba(212,83,126,0.85)', cursor: 'pointer' }}>
                  <span className="hide-mobile">@{user.username}</span>
                  <span className="show-mobile">👤</span>
                </span>
                <button onClick={logout} className="nav-link-btn" style={{ ...navLinkStyle, fontSize: 10 }}>
                  <span className="hide-mobile">Logout</span>
                </button>
              </>
            ) : (
              <button onClick={() => setShowLogin(true)} className="nav-link-btn" style={navLinkStyle}>Sign in</button>
            )}
            <button
              onClick={handleCreateRoom}
              disabled={creatingRoom}
              className="nav-btn-share"
              style={{ ...navBtnStyle, borderColor: 'rgba(147,83,212,0.6)', opacity: creatingRoom ? 0.6 : 1 }}
              onMouseOver={e => { if (!creatingRoom) { e.currentTarget.style.borderColor = 'rgba(147,83,212,1)'; e.currentTarget.style.color = '#a855f7'; }}}
              onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(147,83,212,0.6)'; e.currentTarget.style.color = '#f5e6ed'; }}
            >
              <span className="hide-mobile">{creatingRoom ? 'Creating...' : '🎧 Start a Room'}</span>
              <span className="show-mobile">{creatingRoom ? '...' : '🎧'}</span>
            </button>
            <button onClick={handlePostClick} className="nav-btn-share" style={navBtnStyle}
              onMouseOver={e => { e.currentTarget.style.borderColor = 'rgba(212,83,126,1)'; e.currentTarget.style.color = '#D4537E'; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = 'rgba(212,83,126,0.6)'; e.currentTarget.style.color = '#f5e6ed'; }}
            >
              <span className="hide-mobile">+ Share a Moment</span>
              <span className="show-mobile">+ Share</span>
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="feed-content" style={{ maxWidth: 1400, margin: '0 auto', padding: '48px 24px 24px' }}>
        <p style={{ fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(212,83,126,0.7)', marginBottom: 10 }}>Global feed</p>
        <h1 className="hero-heading" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 36, letterSpacing: '0.02em', lineHeight: 1.15, color: '#f5e6ed', marginBottom: 28 }}>
          That one part of the song<br />
          <em style={{ fontStyle: 'italic', color: '#D4537E' }}>everyone needs to hear.</em>
        </h1>

        {/* Tags filter bar */}
        {allTags.length > 0 && (
          <div style={{ overflowX: 'auto', marginBottom: 24, paddingBottom: 4 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'nowrap' }}>
              <button onClick={() => handleTagClick(null)} style={{
                fontSize: 11, padding: '6px 14px', borderRadius: 20, flexShrink: 0,
                background: !activeTag ? 'rgba(212,83,126,0.2)' : 'transparent',
                border: `0.5px solid ${!activeTag ? '#D4537E' : 'rgba(212,83,126,0.3)'}`,
                color: !activeTag ? '#D4537E' : 'rgba(245,230,237,0.45)',
                cursor: 'pointer', letterSpacing: '0.06em', transition: 'all 0.15s',
              }}>All</button>
              {allTags.map(({ tag, count }) => (
                <button key={tag} onClick={() => handleTagClick(tag)} style={{
                  fontSize: 11, padding: '6px 14px', borderRadius: 20, flexShrink: 0,
                  background: activeTag === tag ? 'rgba(212,83,126,0.15)' : 'transparent',
                  border: `0.5px solid ${activeTag === tag ? '#D4537E' : 'rgba(212,83,126,0.3)'}`,
                  color: activeTag === tag ? '#D4537E' : 'rgba(245,230,237,0.45)',
                  cursor: 'pointer', letterSpacing: '0.06em', transition: 'all 0.15s',
                }}>
                  {tag} <span style={{ opacity: 0.5, fontSize: 10 }}>{count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Feed */}
        {loading ? (
          <div className="moments-grid">
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : moments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 36, marginBottom: 16, opacity: 0.25 }}>♪</div>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 300, color: 'rgba(245,230,237,0.65)', marginBottom: 8 }}>
              {activeTag ? `No moments tagged ${activeTag}` : 'No moments yet'}
            </p>
            <p style={{ fontSize: 11, color: 'rgba(245,230,237,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              {activeTag ? 'Try a different tag' : 'Be the first to share one'}
            </p>
          </div>
        ) : (
          <>
            <div className="moments-grid">
              {visibleMoments.map(m => (
                <LazyCard key={m.id}>
                  <MomentCard moment={m} onTagClick={handleTagClick} />
                </LazyCard>
              ))}
            </div>
            <div ref={loaderRef} style={{ height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 8 }}>
              {loadingMore && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(245,230,237,0.35)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', border: '1.5px solid rgba(212,83,126,0.3)', borderTopColor: '#D4537E', animation: 'spin 0.8s linear infinite' }} />
                  Loading more...
                </div>
              )}
              {!loadingMore && visibleCount >= moments.length && moments.length > PAGE_SIZE && (
                <p style={{ fontSize: 10, color: 'rgba(245,230,237,0.2)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                  ♪ All {moments.length} moments loaded
                </p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <footer style={{ textAlign: 'center', padding: '28px 20px', borderTop: '0.5px solid rgba(212,83,126,0.1)', marginTop: 40 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginBottom: 12 }}>
          <button onClick={() => navigate('/about')} style={{ background: 'none', border: 'none', fontSize: 11, color: 'rgba(245,230,237,0.3)', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer' }}>About</button>
          <span style={{ color: 'rgba(212,83,126,0.2)' }}>·</span>
          <button onClick={() => navigate('/collections')} style={{ background: 'none', border: 'none', fontSize: 11, color: 'rgba(245,230,237,0.3)', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer' }}>Collections</button>
        </div>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, fontSize: 13, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#D4537E', opacity: 0.8, animation: 'glow-pulse 2.5s ease-in-out infinite' }}>
          ♪ Made by Shubh
        </p>
      </footer>

      {showPost    && <PostMoment   onClose={() => setShowPost(false)}    />}
      {showLogin   && <LoginModal   onClose={() => setShowLogin(false)}   />}
      {showProfile && <ProfileModal onClose={() => setShowProfile(false)} />}
      {showWelcome && (
        <WelcomeModal
          onClose={handleWelcomeClose}
          username={user?.username || user?.user_metadata?.username}
        />
      )}
    </div>
  );
}